import React, { memo } from 'react';
import analytics from '@xto10x/core/lib/analytics';
import { isProdEnvironment } from '@xto10x/core/lib/config';
import { SimpleButton as Button, styled } from '@peoplecues/commons';
import TextAreaRaw from './components/textarearaw';
import {
  FlexContainer as Flex,
  Text
} from './components/CommonStyledComponents';
import userApi from './services/userApi';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Illustration = styled.img`
  width: 40vw;
  height: auto;
`;

const Message = styled.p`
  text-align: center;
  margin: 0;
  margin-top: 20px;
  font-size: 18px;
  color: #6d6c6f;
  .wip,
  .checkBackLater {
    font-size: 16px;
    font-weight: 500;
  }
  .checkBackLater {
    /*  */
  }
`;

const Dialog = styled.div`
  z-index: 10;
  display: ${props => (props.open ? 'block' : 'none')};
  position: fixed;
  top: 50%;
  right: 50%;
  transform: translateX(50%) translateY(-50%);
  width: ${props => (props.width ? props.width : '50vw')};
  min-height: 200px;
  background-color: #fff;
  border-radius: 4px;
  max-height: 80vh;
  overflow-y: scroll;
  .errorDetailsContainer {
    max-height: 35vh;
    overflow-y: scroll;
  }
`;

const DialogDivider = styled.div`
  width: 100%;
  height: 1px;
  margin-top: -12px;
  background-color: #c9c9c9;
`;

const DialogBackDrop = styled.div`
  display: ${props => (props.open ? 'block' : 'none')};
  height: 100vh;
  width: 100vw;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 9;
  background-color: rgba(0, 0, 0, 0.5);
`;

const DialogCloseButton = styled.img`
  position: absolute;
  top: 24px;
  right: 24px;
  cursor: pointer;
  height: 24px;
  width: 24px;
`;

const Modal = memo(({ children, width, heading, open, onClose }) => (
  <>
    <DialogBackDrop open={open} />
    <Dialog width={width} open={open}>
      <DialogCloseButton src="/assets/images/close.svg" onClick={onClose} />
      <Text p={24} fontSize={24} fontWeight={500}>
        {heading}
      </Text>
      <DialogDivider />
      {children}
    </Dialog>
  </>
));

const ActionContainer = styled(Flex)`
  .button14 {
    white-space: nowrap;
  }
  .secondButton {
    margin-left: 20px;
  }
`;

const TextArea = styled(TextAreaRaw)`
  width: 100%;
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      errorInfo: null,
      openSupportModal: false,
      issueDescription: '',
      openSuccessModal: false,
      openErrorDetailsModal: false
    };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });
    try {
      analytics.trackError('crash_reports', error);
    } catch (err) {
      //
    }
  }

  handleTryAgain = () => window.location.reload();

  openSupportDialog = () =>
    this.setState(state => ({ ...state, openSupportModal: true }));

  closeSupportDialog = () =>
    this.setState(state => ({ ...state, openSupportModal: false }));

  openSuccessDialog = () =>
    this.setState(state => ({ ...state, openSuccessModal: true }));

  closeSuccessDialog = () =>
    this.setState(state => ({ ...state, openSuccessModal: false }));

  openErrorDetailsDialog = () =>
    this.setState(state => ({ ...state, openErrorDetailsModal: true }));

  closeErrorDetailsDialog = () =>
    this.setState(state => ({ ...state, openErrorDetailsModal: false }));

  onChangeIssueDescription = ({ target: { value } }) =>
    this.setState(state => ({ ...state, issueDescription: value }));

  sendSupportRequest = () => {
    const requestBody = {
      attachments: [],
      description: this.state.issueDescription
        .concat('\n\n')
        .concat(this.state.error.message)
        .concat(this.state.error.stack),
      type: 'BUG'
    };
    userApi
      .sendHelpQuery(requestBody)
      .then(() => {
        this.closeSupportDialog();
        this.openSuccessDialog();
        this.setState(state => ({ ...state, issueDescription: '' }));
      })
      .catch(() => null);
  };

  render() {
    if (this.state.error) {
      return (
        <>
          <Container>
            <Illustration src="/assets/images/page-crash.png" />
            <Message>
              Oops! Something went wrong.
              <br />
              <span className="wip">
                Our team has been notified about the incident and they are
                working on it.
              </span>
              <br />
              <span className="checkBackLater">
                Please try again after sometime.
              </span>
            </Message>
            <ActionContainer mt={40}>
              <Button onClick={this.handleTryAgain}>Try Again</Button>
              <Button className="secondButton" onClick={this.openSupportDialog}>
                Contact Us
              </Button>

              {!isProdEnvironment && (
                <Button
                  className="secondButton"
                  onClick={this.openErrorDetailsDialog}>
                  View Details
                </Button>
              )}
            </ActionContainer>
          </Container>
          <Modal
            heading="Contact Us"
            open={this.state.openSupportModal}
            onClose={this.closeSupportDialog}>
            <Flex mt={16} p={24}>
              <TextArea
                className={`helpDropDownTextArea tag14 ${
                  this.state.issueDescription ? 'color10' : 'color20'
                }`}
                placeholder="Give us some details"
                defaultBorder
                rows="6"
                onValueChange={this.onChangeIssueDescription}
                value={this.state.issueDescription}
                tabIndex="0"
              />
            </Flex>
            <Flex ph={24} justifyContent="flex-end">
              <ActionContainer mt={20} pb={24}>
                <Button onClick={this.closeSupportDialog}>Cancel</Button>
                <Button
                  disabled={!this.state.issueDescription}
                  className="secondButton"
                  onClick={this.sendSupportRequest}>
                  Submit
                </Button>
              </ActionContainer>
            </Flex>
          </Modal>
          <Modal
            width="30vw"
            heading="Success"
            open={this.state.openSuccessModal}
            onClose={this.closeSuccessDialog}>
            <Flex mt={16} p={24} justifyContent="center" alignItems="center">
              <img
                style={{ width: 50, height: 50 }}
                src="/assets/images/success-green-tick.svg"
              />
              <Text pl={16} color="text_normal" fontSize={18}>
                Request has been submitted successfully.
              </Text>
            </Flex>
            <Flex ph={24} justifyContent="flex-end">
              <ActionContainer pt={20} pb={24}>
                <Button onClick={this.closeSuccessDialog}>Close</Button>
              </ActionContainer>
            </Flex>
          </Modal>
          <Modal
            width="70vw"
            heading="Details"
            open={this.state.openErrorDetailsModal}
            onClose={this.closeErrorDetailsDialog}>
            <Flex
              className="errorDetailsContainer"
              flexDirection="column"
              alignItems="flex-start"
              p={24}>
              <pre>{this.state.error.message}</pre>
              <pre>{this.state.error.stack}</pre>
            </Flex>
            <Flex ph={24} justifyContent="flex-end">
              <ActionContainer pt={20} pb={24}>
                <Button onClick={this.closeErrorDetailsDialog}>Close</Button>
              </ActionContainer>
            </Flex>
          </Modal>
        </>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
